import { Action, createAction, handleActions } from 'redux-actions'
import { all, call, put, select, takeLatest } from 'redux-saga/effects'
import { oc } from 'ts-optchain'

import { fetchNoticeList, ICMSNoticeRequest, ICMSNoticeResponse } from '@/api/CmsApi'
import { PaginationRowCnt } from '@/domains/CommonDomain'
import { IRootState } from '@/modules'
import { appSelector } from '@/modules/common/app'
import { createFetchAction } from '@/utils/actionUtils'
import { merge } from '@/utils/arrayUtils'
import { INativeInfo } from '@/utils/bridgeUtils/features/getNativeInfo'
import { apiFailureReducer } from '@/utils/reducerUtils'

export interface INoticeView {
  noticeIndex: number
  isShowMoreButton: boolean
  isShowPartialLoading: boolean
}

export const defaultView: INoticeView = {
  noticeIndex: 0,
  isShowMoreButton: false,
  isShowPartialLoading: true,
}

interface INoticeState extends ICMSNoticeResponse {
  view: INoticeView
}

const initialState: INoticeState = {
  lastItemId: '',
  list: [],
  view: defaultView,
}

export const NOTICE = 'Notice'
export const FETCH_NOTICE_LIST = `${NOTICE}/fetchNoticeList`
export const INCREASE_PAGE = `${NOTICE}/increasePage`
export const SET_VIEW = `${NOTICE}/setView`

export const noticeActions = {
  setView: createAction(SET_VIEW),
  increasePage: createAction(INCREASE_PAGE),
}

export const noticeFetchNoticeListAsync = createFetchAction(FETCH_NOTICE_LIST)

export const reducer = {
  [noticeFetchNoticeListAsync.SUCCESS]: (state: INoticeState, action: Action<any>) => {
    const { list = [], lastItemId = '' } = action.payload

    const merged = merge(state.list, list)

    return {
      ...state,
      list: merged,
      lastItemId,
    }
  },
  [noticeFetchNoticeListAsync.FAILURE]: apiFailureReducer,
  [SET_VIEW]: (state: INoticeState, action: Action<any>) => ({
    ...state,
    view: {
      ...state.view,
      ...action.payload,
    },
  }),
}

export const noticeSelector = {
  lastItemId: (state: IRootState) => oc(state[NOTICE]).lastItemId(''),
  list: (state: IRootState) => oc(state[NOTICE]).list([]),
  view: (state: IRootState) => oc(state[NOTICE]).view(defaultView),
  title: (state: IRootState) => {
    const { noticeIndex } = noticeSelector.view(state)
    const list = noticeSelector.list(state)

    return oc(list[noticeIndex]).title('')
  },
  contents: (state: IRootState) => {
    const { noticeIndex } = noticeSelector.view(state)
    const list = noticeSelector.list(state)

    return oc(list[noticeIndex]).contents('')
  },
  createdAt: (state: IRootState) => {
    const { noticeIndex } = noticeSelector.view(state)
    const list = noticeSelector.list(state)

    return oc(list[noticeIndex]).createdAt()
  },
}

export function* getNoticeList() {
  try {
    yield put(noticeActions.setView({ isShowMoreButton: false, isShowPartialLoading: true }))
    const { language }: INativeInfo = yield select(appSelector.nativeInfo)
    const lastItemId = yield select(noticeSelector.lastItemId)
    const params: ICMSNoticeRequest = {
      count: PaginationRowCnt,
      languageCode: language,
      lastItemId,
    }
    const res: ICMSNoticeResponse = yield call(fetchNoticeList, params)
    yield all([
      put(noticeFetchNoticeListAsync.success(res)),
      put(
        noticeActions.setView({
          isShowMoreButton: false,
          isShowPartialLoading: res.list.length >= PaginationRowCnt,
        }),
      ),
    ])
  } catch (e) {
    yield all([
      put(noticeFetchNoticeListAsync.failure(e)),
      put(
        noticeActions.setView({
          isShowMoreButton: true,
          isShowPartialLoading: false,
        }),
      ),
    ])
  }
}

export const noticeReducer = handleActions(reducer, initialState)
export const noticeSaga = [
  takeLatest(noticeFetchNoticeListAsync.FETCH, getNoticeList),
  takeLatest(INCREASE_PAGE, getNoticeList),
]
